import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';
import {
  MenuGroupHeader,
  MenuItem,
  mergeClasses,
  Button,
} from '@fluentui/react-components';
import { VirtualizerScrollViewDynamic } from '@fluentui/react-virtualizer';
import { ChevronRightRegular } from '@fluentui/react-icons';
import { StatusIcon } from './status-icon';
import { TruncatedText } from './truncated-text';
import { useScopeSelectorStyles } from '../styles';
import { geti18nStringTemplates } from '../helper';
import { IFocusedEntityContext, useFocusedEntityContext } from '../focused-entity-context';
import { Entity } from '../types';

export const ScopeMenuItem = ({ entity, addScope }) => {
  const {
    name,
    status,
    entityTypeDisplayName,
    id,
  } = entity;

  const styles = useScopeSelectorStyles();

  const statusIcon = (entityStatus) => (entityStatus
    ? {
      icon: (<StatusIcon status={entityStatus} />),
    }
    : null);

  return (
    <MenuItem
      className={mergeClasses(styles.menuItem, styles.scopeMenuItem, styles.hover, `entity-id-${id}`, 'scope-menu-item')}
      aria-label={`${status} ${entityTypeDisplayName}: ${name}}`}
      onClick={() => addScope(entity)}
      {...statusIcon(status)}
    >
      {name}
    </MenuItem>
  );
};

ScopeMenuItem.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string,
    status: PropTypes.string,
    entityTypeDisplayName: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  addScope: PropTypes.func.isRequired,
};

export interface IScopeMenuItemListHeaderProps {
  i18n: { getString: (key: string, params?: any) => string };
  rootEntityTypeDisplayName: string;
  selectedEntity: Entity;
  removeScope: (entity?: Entity) => void;
  addScope: (entity: Entity) => void;
  setPopoverOpen: (open: boolean) => void;
  hasRootEntityFilter: boolean;
  hasSubEntityFilter: boolean;
}

export const ScopeMenuItemListHeader = (props: IScopeMenuItemListHeaderProps) => {
  const {
    i18n,
    rootEntityTypeDisplayName,
    selectedEntity,
    removeScope,
    addScope,
    setPopoverOpen,
    hasRootEntityFilter,
    hasSubEntityFilter,
  } = props;

  const { focusedEntity } = useFocusedEntityContext();
  const styles = useScopeSelectorStyles();
  const i18nTemplates = geti18nStringTemplates(i18n);
  const rootEntityHeaderText = useMemo(
    () => (hasRootEntityFilter
      ? i18nTemplates.filteredEntity(rootEntityTypeDisplayName)
      : i18nTemplates.allEntity(rootEntityTypeDisplayName)),
    [hasRootEntityFilter, i18nTemplates, rootEntityTypeDisplayName],
  );

  const Header = useCallback(() => {
    if (focusedEntity) {
      if (focusedEntity === 'All') {
        return (
          <button
            type="button"
            className="scope-breadcrumb-all"
            onClick={() => {
              if (setPopoverOpen) {
                setPopoverOpen(false);
              }
              removeScope();
            }}
          >
            <TruncatedText
              content={rootEntityHeaderText}
            />
          </button>
        );
      }

      return (
        <button
          type="button"
          onClick={() => {
            if (setPopoverOpen) {
              setPopoverOpen(false);
            }
            addScope(focusedEntity);
          }}
        >
          <TruncatedText
            content={
              focusedEntity.subEntityTypeDisplayName
                ? i18nTemplates.subEntities(focusedEntity.name, focusedEntity.subEntityTypeDisplayName, hasSubEntityFilter)
                : focusedEntity.name
            }
          />
        </button>
      );
    }

    if (selectedEntity) {
      const listHeaderEntity = selectedEntity.subEntityTypeDisplayName
        ? selectedEntity
        : selectedEntity.parentEntity;
      return (
        <button
          type="button"
          className="scope-remove-scope"
          onClick={() => {
            if (setPopoverOpen) {
              setPopoverOpen(false);
            }
            removeScope(selectedEntity);
          }}
        >
          <TruncatedText
            content={
              listHeaderEntity.subEntityTypeDisplayName
                ? i18nTemplates.subEntities(listHeaderEntity.name, listHeaderEntity.subEntityTypeDisplayName, hasSubEntityFilter)
                : rootEntityHeaderText
            }
          />
        </button>
      );
    }

    return (
      <TruncatedText content={rootEntityHeaderText} />
    );
  }, [addScope, focusedEntity, i18nTemplates, removeScope, rootEntityTypeDisplayName, selectedEntity, setPopoverOpen, rootEntityHeaderText]);

  return (
    <MenuGroupHeader>
      <span className={mergeClasses('scope-menu-item-list-header', styles.entityListHeader)}>
        <Header />
      </span>
    </MenuGroupHeader>
  );
};

const ItemApproximateHeight = 33;
export interface IScopeMenuItemListListLoaderProps {
  i18n: { getString: (key: string, params?: any) => string };
  entityList: Entity[];
  addScope: (entity: Entity) => void;
  entityTypeDisplayName: string;
}

export const ScopeMenuItemListLoader = (props: IScopeMenuItemListListLoaderProps) => {
  const {
    i18n, entityList, addScope, entityTypeDisplayName,
  } = props;

  const { setFocusedEntity } = useFocusedEntityContext() as IFocusedEntityContext;

  const stringTemplates = geti18nStringTemplates(i18n);
  const styles = useScopeSelectorStyles();

  if (!entityList) {
    return (
      <MenuItem className={mergeClasses(styles.menuItem, styles.hover)}>
        {entityTypeDisplayName
          ? stringTemplates.loadingEntity(entityTypeDisplayName)
          : stringTemplates.Loading}
      </MenuItem>
    );
  }

  if (entityList.length === 0) {
    return (
      <MenuItem className={mergeClasses(styles.menuItem, styles.hover)}>
        {entityTypeDisplayName
          ? stringTemplates.noEntityFound(entityTypeDisplayName)
          : stringTemplates.NotFound}
      </MenuItem>
    );
  }

  return (
    <VirtualizerScrollViewDynamic
      numItems={entityList.length}
      itemSize={ItemApproximateHeight}
    >
      { (index) => {
        const entity = entityList[index];
        if (entity.subEntityTypeDisplayName) {
          return (
            <div key={entity.id} className={mergeClasses(styles.customMenuSplitGroup, 'custom-menu-split-group', `entity-id-${entity.id}`)}>
              <ScopeMenuItem entity={entity} addScope={addScope} />
              <div
                className="custom-split-menu-group-icon"
              >
                <Button
                  className={mergeClasses(styles.scopeButton, styles.hover, 'scope-button')}
                  appearance="subtle"
                  icon={<ChevronRightRegular />}
                  onClick={(_e) => {
                    setFocusedEntity(entity);
                  }}
                />
              </div>
            </div>
          );
        }
        return <ScopeMenuItem key={entity.id} entity={entity} addScope={addScope} />;
      }}
    </VirtualizerScrollViewDynamic>
  );
};
